// src/common/ReadMoreText.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ReadMoreText.css';

/**
 * ReadMoreText
 * - Clamps to N lines on mobile/desktop
 * - Shows "Read more" ONLY if the text actually overflows
 * - Preserves user line breaks and wraps long tokens/URLs
 *
 * Props:
 *   text: string
 *   parseFn?: (text: string) => ReactNode
 *   suffix?: ReactNode                // ✅ NEW: e.g. "(edited)"
 *   inline?: boolean                  // wrap with <span> instead of <div>
 *   lineClampMobile?: number          // default 5
 *   lineClampDesktop?: number         // default 8
 */
export default function ReadMoreText({
  text = '',
  parseFn = null,
  suffix = null, // ✅ NEW
  inline = false,
  lineClampMobile = 5,
  lineClampDesktop = 8,
}) {
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef(null);

  const Wrapper = inline ? 'span' : 'div';
  const safeText = text || '';

  // Render text (with mention parsing if provided)
  const rendered = useMemo(() => {
    return typeof parseFn === 'function' ? parseFn(safeText) : safeText;
  }, [safeText, parseFn]);

  // Track viewport to decide clamp
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  const clampLines = isMobile ? lineClampMobile : lineClampDesktop;

  // ✅ Always measure both unclamped and clamped heights, then restore visual state
  const measureOverflow = () => {
    const el = contentRef.current;
    if (!el) return;

    // Save current inline styles
    const prev = {
      display: el.style.display,
      webkitBoxOrient: el.style.webkitBoxOrient,
      webkitLineClamp: el.style.webkitLineClamp,
      overflow: el.style.overflow,
    };

    // 1) Unclamped: get full height
    el.style.display = 'block';
    el.style.webkitBoxOrient = '';
    el.style.webkitLineClamp = '';
    el.style.overflow = 'visible';
    const fullHeight = el.scrollHeight;

    // 2) Clamped (regardless of expanded): get clamped height
    el.style.display = '-webkit-box';
    el.style.webkitBoxOrient = 'vertical';
    el.style.webkitLineClamp = String(clampLines);
    el.style.overflow = 'hidden';
    const clampedHeight = el.clientHeight;

    // 3) Restore visual state (expanded = show full, else clamped)
    if (expanded) {
      el.style.display = 'block';
      el.style.webkitBoxOrient = '';
      el.style.webkitLineClamp = '';
      el.style.overflow = 'visible';
    } else {
      el.style.display = '-webkit-box';
      el.style.webkitBoxOrient = 'vertical';
      el.style.webkitLineClamp = String(clampLines);
      el.style.overflow = 'hidden';
    }

    // Restore any other saved styles (safety)
    // (display/orient/clamp/overflow are set above intentionally)
    void prev;

    // 4) Decide if we should show the toggle
    setHasOverflow(fullHeight > clampedHeight + 1);
  };

  // Re-measure on text, clamp changes, expanded toggle, and resize
  useEffect(() => {
    const ro = new ResizeObserver(() => measureOverflow());
    if (contentRef.current) ro.observe(contentRef.current);

    // initial measure
    measureOverflow();

    const onResize = () => measureOverflow();
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, clampLines, expanded]);

  return (
    <Wrapper className="readmore-wrapper">


<span
  ref={contentRef}
  className={`readmore-text ${expanded ? 'expanded' : 'clamped'}`}
  style={expanded ? undefined : { WebkitLineClamp: clampLines }}
  onClick={(e) => {
    if (e.target.closest && e.target.closest('a.mention-link')) return;
  }}
>
  {rendered}
  {!!suffix && (expanded || !hasOverflow) && (
    <span className="readmore-suffix">{suffix}</span>
  )}
</span>



      {hasOverflow && (
        <button
          type="button"
          className="readmore-toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </Wrapper>
  );
}
